// Ported from case-desk src/i18n/i18n.ts (commit 1d18e32) per docs/architecture/das-web.md § 14 on
// 2026-08-18: i18next from the first commit, only translated visible strings, `de-DE` plus `en` with a
// typed key tree. The language follows the browser by default; a *browser-wide* preference store is
// deliberately not introduced (das-web § 6/§ 9) — same reasoning as tenant selection never living in
// `localStorage` (das-web § 6). The manual toggle (2026-08-26, LanguageToggle) persists to
// `sessionStorage` instead: scoped to the one tab it was set in, so two tabs of the same identity can
// still show different languages, the same way they may show different tenants.
//
// LANGUAGE BUNDLES LOAD PER LANGUAGE (das-web § 11: bundle size is a release gate). Shipping every
// language to every reader makes the initial download grow with languages × modules, and each reader
// uses exactly one of them. `de-DE` stays resident because it is the `fallbackLng` and must be able to
// answer a missing key at any moment; every other language is fetched on demand. `initialLanguageReady`
// resolves once the language this tab will actually show is present, so the application can render
// translated text on its first paint instead of flashing the fallback.
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { deDE } from './de-DE';

export const supportedLanguages = ['de-DE', 'en'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

/** The lazily fetched bundles. `de-DE` is absent on purpose: it ships with the application. */
const lazyBundles: Partial<Record<SupportedLanguage, () => Promise<object>>> = {
  en: async () => (await import('./en')).en,
};

export function detectLanguage(browserLanguages: readonly string[]): SupportedLanguage {
  for (const language of browserLanguages) {
    if (language.toLowerCase().startsWith('en')) {
      return 'en';
    }
    if (language.toLowerCase().startsWith('de')) {
      return 'de-DE';
    }
  }
  return 'de-DE';
}

const languageSessionKey = 'das-web-language';

export function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return (supportedLanguages as readonly string[]).includes(value ?? '');
}

/** The this-tab override set via `setLanguage`, if any — read defensively since storage access can
 * throw (private browsing, blocked site data). */
function readSessionLanguage(): SupportedLanguage | undefined {
  try {
    const stored = sessionStorage.getItem(languageSessionKey);
    return isSupportedLanguage(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export const i18n = i18next.createInstance();

// The stored override wins over the browser, so a reload of this tab keeps the chosen language — and
// it is also the language whose bundle the first paint has to wait for.
const startupLanguage =
  readSessionLanguage() ?? detectLanguage(typeof navigator === 'undefined' ? [] : navigator.languages);

void i18n.use(initReactI18next).init({
  fallbackLng: 'de-DE',
  interpolation: {
    escapeValue: false,
  },
  lng: startupLanguage,
  resources: { 'de-DE': { translation: deDE } },
  returnNull: false,
});

/** Fetches a language bundle once and registers it; resolves immediately when it is already present. */
async function ensureBundle(language: SupportedLanguage): Promise<void> {
  if (i18n.hasResourceBundle(language, 'translation')) {
    return;
  }

  const load = lazyBundles[language];
  if (!load) {
    return;
  }

  i18n.addResourceBundle(language, 'translation', await load(), true, true);
}

/**
 * Resolves once the bundle of the language this tab will show is registered. `main.tsx` waits for it
 * before the first render; for `de-DE` it is already satisfied and settles in a microtask.
 */
export const initialLanguageReady: Promise<void> = ensureBundle(startupLanguage);

/**
 * Switches the display language for the current tab, remembers it in `sessionStorage` so a reload of
 * that same tab keeps it, and keeps the document language in sync so screen readers and hyphenation
 * follow along (das-web § 12). Awaits the bundle first, so the UI never shows the fallback language in
 * place of the chosen one.
 */
export async function setLanguage(language: SupportedLanguage): Promise<void> {
  try {
    sessionStorage.setItem(languageSessionKey, language);
  } catch {
    // Storage blocked — the language of the current view still changes below.
  }

  await ensureBundle(language);
  await i18n.changeLanguage(language);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}
